/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { CaptionPlayback } from '@components/editors/caption-editor/body-right/caption-playback.tsx';
import CaptionAbout from '@components/editors/caption-editor/caption-about.tsx';
import { SpeakersVoicePanel } from '@components/editors/shared/voice-profiles/speakers-voice-panel.tsx';
import { JumpToViewWord } from '@components/editors/jump-to-view-word.tsx';
import CaptionSegment from '@components/editors/caption-editor/body/caption-segment.tsx';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { AudioWaveform } from '@components/editors/audio-waveform.tsx';
import {
  Components as VirtuosoComponents,
  Virtuoso,
  VirtuosoHandle
} from 'react-virtuoso';
import { indexOf } from 'lodash';
import { getCurrentSegmentSeeking } from '@utils/editors/get-current-segment-seeking.ts';
import { useAudio } from '@utils/providers/audio-context.tsx';

import { useInterval } from '@utils/hooks/use-interval.tsx';
import { useMediaQuery } from '@utils/hooks/use-media-query.tsx';
import { CaptionEditorProvider } from '@utils/providers/caption-editor-context.tsx';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import { usePrompt } from '@components/editors/confirm/prompt.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useConcurrentEditPolling } from '@utils/hooks/use-concurrent-edit-polling.ts';
import ConcurrentEditBanner from '@components/editors/concurrent-edit-banner.tsx';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '@utils/api/query-keys.ts';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetSessionDocuments } from '@utils/api/documents/use-get-session-documents.ts';
import EditableSplitCaptionPanel from '@components/editors/caption-editor/split-view/editable-split-caption-panel.tsx';
import { CAPTION_ROW_HEIGHT } from '@components/editors/caption-editor/split-view/pair-source-segment.ts';
import { FloatingVideoOverlay } from '@components/editors/shared/media/floating-video-overlay.tsx';
import { CollapsedMediaBar } from '@components/editors/shared/media/collapsed-media-bar.tsx';
import { CaptionFindReplaceHost } from '@components/editors/caption-editor/caption-find-replace-host.tsx';
import { AnchoredOverlay } from '@components/editors/shared/find-replace/anchored-overlay.tsx';
import { useCaptionHotkeys } from '@utils/providers/caption-hotkey-context.tsx';
import { TranslationCaptionHandle } from '@utils/providers/translation-caption-context.tsx';
import { CaptionTrainingDialogProvider } from '@components/editors/caption-editor/body/caption-training-dialog.tsx';
import { CommentsProvider } from '@components/editors/shared/comments/comments-context.tsx';
import { CommentsPopover } from '@components/editors/shared/comments/comments-popover.tsx';
import { CommentFilterProvider } from '@components/editors/shared/comments/comments-filter-context.tsx';

export default function CaptionMain() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { currentTime, linkedPlayback, peaksInstance } = useAudio();
  const currentWordRef = useRef<HTMLSpanElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  void 0;

  const {
    document,
    cleanup,
    query,
    session,
    documentResult,
    autoSave,
    isDirty,
    undo,
    redo,
    documentVersion,
    saveStatus,
    saveDocument,
    reloadFromServer,
    search
  } = useCaption();

  const queryClient = useQueryClient();
  const { documentId: routeDocumentId } = useParams<{
    sessionId: string;
    documentId: string;
  }>();
  const sessionId = session?.session_id ?? '';
  const documentId = documentResult?.result?.id ?? '';

  const splitScrollerRef = useRef<HTMLElement | null>(null);
  const isSyncingScroll = useRef(false);

  // Fetch session documents for split view
  const { data: sessionDocs } = useGetSessionDocuments(sessionId || undefined);
  const translatedDocuments = useMemo(
    () =>
      (sessionDocs?.result ?? []).filter(
        doc =>
          doc.id !== (routeDocumentId ?? documentId) && doc.type === 'caption'
      ),
    [sessionDocs, routeDocumentId, documentId]
  );
  const hasTranslations = translatedDocuments.length > 0;

  // Match the transcript editor (#2235): layout follows a URL query
  // param. `?lang=XX` opens split view with that translation on the
  // right; no param → single pane. Replaces the old localStorage
  // `SPLIT_VIEW_KEY` + Columns2 toggle so the dialog alone drives the
  // layout — picking a translation enters split, picking Original
  // leaves it, no separate button to flip.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlLang = searchParams.get('lang');
  const selectedTranslationId = useMemo(() => {
    if (!urlLang) return null;
    const match = translatedDocuments.find(
      d => (d.language ?? '').toUpperCase() === urlLang.toUpperCase()
    );
    return match?.id ?? null;
  }, [urlLang, translatedDocuments]);
  const splitView = selectedTranslationId != null && hasTranslations;

  // Writer used by the language dialog. Picking the source doc or
  // the current primary drops the lang param, which collapses the
  // layout to single pane. Mirrors transcript-main's defensive check
  // against `documentId` so a translation-primary user clicking
  // Original doesn't accidentally match the source doc in the
  // translations list and set `?lang=<source-lang>`.
  const handleTranslationChange = useCallback(
    (docId: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (!docId || docId === documentId || docId === routeDocumentId) {
        next.delete('lang');
      } else {
        const translation = translatedDocuments.find(d => d.id === docId);
        if (translation?.language) {
          next.set('lang', translation.language.toUpperCase());
        } else {
          next.delete('lang');
        }
      }
      setSearchParams(next);
    },
    [
      searchParams,
      setSearchParams,
      translatedDocuments,
      documentId,
      routeDocumentId
    ]
  );

  // Ref to access translation pane's save/isDirty
  const translationHandleRef = useRef<TranslationCaptionHandle>(null);
  // #2250 — slots each source row fills in split view, reported by the
  // translation pane. Index N is source segment N.
  const [rowSpans, setRowSpans] = useState<number[]>([]);
  // In split view the header Undo / Redo act on the translation, the only
  // editable pane. The source pane is read-only and has no history to undo.
  const handleTranslationUndo = useCallback(
    () => translationHandleRef.current?.undo(),
    []
  );
  const handleTranslationRedo = useCallback(
    () => translationHandleRef.current?.redo(),
    []
  );
  const { vars, cls } = useThemeVars();
  // Mirror of the translation pane's dirty flag. The ref carries it too,
  // but ref mutations don't re-render — so the beforeunload prompt and
  // the dual-save gating below need a reactive copy.
  const [translationIsDirty, setTranslationIsDirty] = useState(false);

  // Dual save: save the panes that actually have unsaved edits. Passed
  // to CaptionAbout so the toolbar Save button writes the translation
  // pane too (otherwise the right-side edits in split view never reach
  // the server). Skipping clean panes avoids the double success toast
  // when the user only edited one side — the common case, since the
  // source pane is read-only while split view is active.
  const handleSaveBoth = useCallback(
    (userAction?: boolean) => {
      if (isDirty) saveDocument(userAction);
      if (translationHandleRef.current?.isDirty) {
        translationHandleRef.current.saveDocument(userAction);
      }
    },
    [saveDocument, isDirty]
  );

  // Also save translation pane on Ctrl+S (source is already handled by hotkey provider)
  useEffect(() => {
    if (!splitView || !hasTranslations) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        translationHandleRef.current?.saveDocument(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [splitView, hasTranslations]);

  // Pixel-level scroll sync: mirror left panel scrollTop to right panel
  const sourceScrollElRef = useRef<HTMLElement | null>(null);

  const handleSourceScroll = useCallback((e: Event) => {
    if (isSyncingScroll.current) return;
    const target = e.target as HTMLElement;
    if (splitScrollerRef.current) {
      isSyncingScroll.current = true;
      splitScrollerRef.current.scrollTop = target.scrollTop;
      requestAnimationFrame(() => {
        isSyncingScroll.current = false;
      });
    }
  }, []);

  // Bidirectional: right pane → left pane scroll sync
  const handleSplitScroll = useCallback((e: Event) => {
    if (isSyncingScroll.current) return;
    const target = e.target as HTMLElement;
    if (sourceScrollElRef.current) {
      isSyncingScroll.current = true;
      sourceScrollElRef.current.scrollTop = target.scrollTop;
      requestAnimationFrame(() => {
        isSyncingScroll.current = false;
      });
    }
  }, []);

  // Attach right→left scroll listener when split scroller mounts
  useEffect(() => {
    const el = splitScrollerRef.current;
    if (!el || !splitView || !hasTranslations) return;
    el.addEventListener('scroll', handleSplitScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleSplitScroll);
  }, [splitView, hasTranslations, handleSplitScroll, selectedTranslationId]);

  const sourceScrollerRef = useCallback(
    (el: HTMLElement | Window | null) => {
      // Clean up previous listener
      if (sourceScrollElRef.current) {
        sourceScrollElRef.current.removeEventListener(
          'scroll',
          handleSourceScroll
        );
      }

      if (el && el instanceof HTMLElement) {
        el.addEventListener('scroll', handleSourceScroll, { passive: true });
        sourceScrollElRef.current = el;
      } else {
        sourceScrollElRef.current = null;
      }
    },
    [handleSourceScroll]
  );

  const { hasRemoteEdit, dismiss, reload } = useConcurrentEditPolling({
    sessionId,
    documentId,
    initialLastModified: documentResult?.result?.last_modified ?? new Date(),
    isSaving: saveStatus === 'saving',
    enabled: !!session && !!documentResult && !documentResult.result.finished
  });

  const hasRemoteEditRef = useRef(false);
  hasRemoteEditRef.current = hasRemoteEdit;

  // Guard autosave: skip server sync when remote edit notification is active
  const guardedAutoSave = useCallback(() => {
    if (!hasRemoteEditRef.current) {
      autoSave();
      // Also auto-save the translation pane if split view is active
      translationHandleRef.current?.autoSave();
    }
  }, [autoSave]);
  useInterval(guardedAutoSave, 120 * 1000);

  const handleReload = useCallback(async () => {
    const freshDoc = await reload();
    if (freshDoc) {
      queryClient.setQueryData(
        QueryKeys.document(sessionId, documentId),
        freshDoc
      );
      reloadFromServer(freshDoc);
    }
  }, [reload, queryClient, sessionId, documentId, reloadFromServer]);

  const handleSaveMyChanges = useCallback(() => {
    saveDocument(true);
    dismiss();
  }, [saveDocument, dismiss]);

  useEffect(() => {
    const isEditableTarget = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName?.toLowerCase();
      const isFormField =
        tag === 'input' ||
        tag === 'textarea' ||
        (el as HTMLElement).isContentEditable;
      return isFormField;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      if (!ctrlOrMeta) return;
      if (isEditableTarget(e.target)) return; // let Slate / inputs handle it

      const k = e.key.toLowerCase();

      // Ctrl/Cmd + Z → undo
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }

      // Ctrl/Cmd + Shift + Z OR Ctrl/Cmd + Y → redo
      if ((k === 'z' && e.shiftKey) || k === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [undo, redo]);

  const items = useMemo(() => {
    return (
      document?.document?.filter(item =>
        item.text?.toLowerCase().includes(query.toLowerCase())
      ) || []
    );
  }, [document?.document, query]);

  const [showJumpButton, setShowJumpButton] = useState<'up' | 'down' | false>(
    false
  );
  const visibleRangeRef = useRef<{ startIndex: number; endIndex: number }>({
    startIndex: 0,
    endIndex: 0
  });
  const [enabled, setEnabled] = useState<boolean>(
    documentResult?.result?.finished ?? false
  );

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  usePrompt({ isDirty: isDirty || translationIsDirty });

  const onWordOutOfView = (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => {
    currentWordRef.current = ref;
    setShowJumpButton(direction);
  };

  const onWordInView = () => setShowJumpButton(false);

  // Search filtering removed in #2233 — the shared Find & Replace
  // popover surfaces matches in its own result list, so the segment
  // list stays fully visible while the user searches. This also
  // matches the transcript editor's behaviour.
  const isSearchActive = Boolean(search?.trim());
  const filteredItems = items;

  // Range-based fallback: when the current segment is virtualized out of the
  // DOM, the per-segment IntersectionObserver can't fire, so derive the jump
  // button state from Virtuoso's visible range and the current segment index.
  useEffect(() => {
    if (!linkedPlayback) {
      setShowJumpButton(false);
      return;
    }
    const current = getCurrentSegmentSeeking(filteredItems, currentTime);
    if (!current) return;
    const idx = indexOf(filteredItems, current);
    if (idx < 0) return;
    const { startIndex, endIndex } = visibleRangeRef.current;
    if (idx < startIndex) setShowJumpButton('up');
    else if (idx > endIndex) setShowJumpButton('down');
  }, [currentTime, filteredItems, linkedPlayback]);

  const jumpToCurrentWord = useCallback(() => {
    const item = getCurrentSegmentSeeking(items, currentTime);
    if (item && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: indexOf(items, item),
        align: 'center',
        behavior: 'smooth'
      });
    }
    setShowJumpButton(false);
  }, [items, currentTime]);

  const renderRow = useCallback(
    (index: number, segment: DocumentSubtitleSegmentV1) => {
      if (!segment) return null;

      const row = (
        <CaptionSegment
          key={`v${documentVersion}-${segment.start}-${segment.stop}-${segment.id}`}
          index={index}
          segment={segment}
          onWordOutOfView={onWordOutOfView}
          onWordInView={onWordInView}
          filteredItems={filteredItems}
          // In split view the source pane is the original — lock it so
          // the translator can only edit the right side.
          readOnly={splitView && hasTranslations}
        />
      );
      // #2250 — when the backend split this segment into several
      // translated rows, pad the source row to the same height so the
      // rows after it stay level with their translations.
      const span = splitView && hasTranslations ? (rowSpans[index] ?? 1) : 1;
      if (span <= 1) return row;
      return (
        <div>
          {row}
          <div
            aria-hidden="true"
            className={cn(
              'border border-t-0',
              cls.border,
              'bg-[var(--card-bg)]'
            )}
            style={{ height: (span - 1) * CAPTION_ROW_HEIGHT }}
          />
        </div>
      );
    },
    [
      rowSpans,
      cls.border,
      filteredItems,
      onWordOutOfView,
      onWordInView,
      documentVersion,
      splitView,
      hasTranslations
    ]
  );

  const vComponents: VirtuosoComponents<DocumentSubtitleSegmentV1, unknown> =
    useMemo(
      () => ({
        Item: ({ children, ...props }) => <div {...props}>{children}</div>
      }),
      []
    );

  // Skip the peaks.js waveform on mobile: the whole 11h waveform buffer (held in
  // memory, cached at 4 zoom levels) is a mobile-specific memory sink, and its
  // container not rendering also prevents peaks init. Falls back to the existing
  // no-waveform layout. Playback/seeking use the media slider, not the waveform.
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const hasWave = !!session?.wave_info_url && !isMobile;
  // Peaks needs the container in the DOM with real dimensions to initialize,
  // so we can't just conditionally render on peaksInstance (chicken-and-egg).
  // Instead: render the wrapper unconditionally when we expect a waveform,
  // give peaks a short grace window to succeed, and if it doesn't come back
  // ready, collapse the wrapper so no empty strip remains. This handles the
  // sessions where wave_info_url is set but the .dat fetch or peaks init
  // silently fails.
  const [waveFailed, setWaveFailed] = useState(false);
  useEffect(() => {
    if (!hasWave) return;
    if (peaksInstance) {
      setWaveFailed(false);
      return;
    }
    const timer = setTimeout(() => {
      if (!peaksInstance) setWaveFailed(true);
    }, 4000);
    return () => clearTimeout(timer);
  }, [hasWave, peaksInstance]);
  const showWave = hasWave && !waveFailed;

  return (
    <CaptionEditorProvider>
      <CaptionTrainingDialogProvider filteredItems={filteredItems}>
        <CommentFilterProvider>
          <div
            className={cn(
              'flex flex-col h-screen overflow-y-auto',
              !(splitView && hasTranslations) && 'lg:overflow-y-hidden',
              cls.pageBg,
              cls.text
            )}
            style={vars}
          >
            {hasRemoteEdit && (
              <ConcurrentEditBanner
                onReload={handleReload}
                onDismiss={dismiss}
                onSaveMyChanges={handleSaveMyChanges}
                hasUnsavedChanges={isDirty}
              />
            )}
            <CaptionAbout
              enabled={enabled}
              setEnabled={setEnabled}
              hasTranslations={hasTranslations}
              translatedDocuments={translatedDocuments}
              selectedTranslationId={selectedTranslationId}
              onTranslationChange={handleTranslationChange}
              onSave={splitView && hasTranslations ? handleSaveBoth : undefined}
              onUndo={
                splitView && hasTranslations ? handleTranslationUndo : undefined
              }
              onRedo={
                splitView && hasTranslations ? handleTranslationRedo : undefined
              }
            />
            {/* #2355 — the caption layout has no speaker label, so identity
            review and enrollment live in a compact, collapsed-by-default
            panel. It renders nothing unless voice profiles are available to
            this user, so editors that never use the feature see no change. */}
            <div className="px-4">
              <SpeakersVoicePanel session={session} documentId={documentId} />
            </div>

            {/* Editor grid fills the remaining vertical space between the top
            About bar and the (optional) waveform. Using flex-1 + min-h-0
            instead of a hand-tuned ``calc(100vh - Npx)`` was to fix the
            empty gray strip at the bottom on sessions with no ``wave_info_url``
            — the old magic number reserved room for a waveform that never
            rendered. */}
            <div
              className={cn(
                'mt-1 flex-1 min-h-0 overflow-hidden md:overflow-y-auto px-4'
              )}
            >
              {document && (
                <div
                  className={cn(
                    'grid h-full',
                    splitView && hasTranslations
                      ? 'grid-cols-1 lg:grid-cols-2'
                      : 'grid-cols-1 md:grid-cols-1 lg:grid-cols-2'
                  )}
                >
                  {/* #2250 — source-pane comments provider. Scoped to the
                    source document id; read-only in split view so the
                    source pane matches the transcript's split-view rule
                    (source is a preview, commenting happens on the
                    translation). Modal lives inside so the caption
                    segments can call `openView` / `openCompose` via
                    `useCommentsContext()` in `scriptix-caption-editor`.
                    `getAnchoredText` maps a comment's [from, to] range
                    to the source segment's text — that's what shows up
                    as the quoted preview inside the modal. */}
                  <CommentsProvider
                    sessionId={sessionId}
                    documentId={documentId}
                    readOnly={splitView && hasTranslations}
                    getAnchoredText={(from, to) => {
                      const seg = filteredItems.find(
                        s =>
                          String(s.start) === (from ?? '') &&
                          String(s.stop) === (to ?? '')
                      );
                      return seg?.text ?? '';
                    }}
                  >
                    {/* Inline caption toolbar retired in #2233 — search moved
                      to the shared Find & Replace popover in the header. */}
                    <div
                      className={cn(
                        'relative flex flex-col min-h-0 m-2 rounded-xl border overflow-hidden shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]',
                        cls.border
                      )}
                      style={{
                        backgroundColor: getColour('secondLayer')
                      }}
                    >
                      {splitView && hasTranslations && (
                        // Matches the translation pane's 44px header so both
                        // columns line up; makes the read-only nature obvious.
                        <header
                          className="flex items-center justify-between gap-2 px-3 py-1 h-11 shrink-0 border-b"
                          style={{ borderColor: getColour('border') }}
                        >
                          <span
                            className="text-xs font-semibold uppercase tracking-wider"
                            style={{ color: getColour('inactiveText') }}
                          >
                            {t('original')}
                          </span>
                          <span
                            className="text-[11px] px-1.5 py-0.5 rounded-full border"
                            style={{
                              borderColor: getColour('border'),
                              color: getColour('inactiveText'),
                              backgroundColor: getColour('secondLayer')
                            }}
                          >
                            {t('read-only')}
                          </span>
                        </header>
                      )}
                      <div
                        role="list"
                        className="overflow-y-auto h-full mb-0.5 min-h-0"
                        style={{
                          minHeight: '200px'
                        }}
                      >
                        {filteredItems.length === 0 ? (
                          <div className="flex items-center justify-center h-full p-8">
                            <div
                              className={cn('text-center opacity-60', cls.text)}
                            >
                              <p className="text-sm">
                                {isSearchActive
                                  ? `No segments match "${search}"`
                                  : 'No segments available'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <Virtuoso
                            data={filteredItems}
                            ref={virtuosoRef}
                            totalCount={filteredItems.length}
                            style={{
                              height: '100%',
                              width: '100%',
                              overflowX: 'hidden',
                              minHeight: '34vh'
                            }}
                            components={vComponents}
                            itemContent={renderRow}
                            // Cap initial synchronous mount: initialItemCount is an
                            // SSR-hydration hint that mounts exactly this many rows on
                            // first render. Each row is a full Slate editor, so passing
                            // the whole length (~9k on an 11h doc) mounted every editor
                            // at once — freezing the main thread and OOM-killing mobile
                            // Safari on open. A small window virtualizes the rest.
                            initialItemCount={Math.min(
                              filteredItems.length,
                              20
                            )}
                            increaseViewportBy={200}
                            defaultItemHeight={CAPTION_ROW_HEIGHT}
                            overscan={20}
                            scrollerRef={sourceScrollerRef}
                            rangeChanged={range => {
                              visibleRangeRef.current = range;
                            }}
                          />
                        )}
                      </div>

                      <JumpToViewWord
                        visible={showJumpButton}
                        onJumpToWord={jumpToCurrentWord}
                        height={hasWave ? 140 : 54}
                      />
                    </div>
                    {/* Caption normal view uses the popover variant
                        instead of the always-visible gutter: the
                        right side of the editor belongs to the media
                        player and the gutter was overlaying the
                        video. The popover shows one card at a time
                        anchored next to the clicked leaf marker, same
                        pattern as the pre-gutter design. Split view
                        still uses the gutter — it has a dedicated
                        right pane and the layout accommodates it. */}
                    <CommentsPopover scrollRootRef={sourceScrollElRef} />
                  </CommentsProvider>
                  {splitView && hasTranslations ? (
                    <EditableSplitCaptionPanel
                      session={session!}
                      sessionId={sessionId}
                      scrollerRef={splitScrollerRef}
                      hasWave={hasWave}
                      selectedDocId={selectedTranslationId}
                      handleRef={translationHandleRef}
                      onDirtyChange={setTranslationIsDirty}
                      sourceSearch={search}
                      sourceSegments={items}
                      onRowSpansChange={setRowSpans}
                    />
                  ) : (
                    <div className="relative flex flex-col min-h-0 p-2">
                      <CaptionPlayback />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {splitView && hasTranslations && (
            <>
              <CollapsedMediaBar hasMedia={!!session?.keep_media} />
              <FloatingVideoOverlay
                hasVideo={
                  !!session?.media_url && session?.media_type !== 'audio'
                }
                sessionId={session?.session_id}
                defaultCorner="bottom-left"
              >
                <CaptionPlayback hideControls />
              </FloatingVideoOverlay>
              {!(!!session?.media_url && session?.media_type !== 'audio') && (
                <div className="hidden">
                  <CaptionPlayback hideControls />
                </div>
              )}
            </>
          )}

          {hasWave && (
            <div
              className={cn(
                'w-full overflow-hidden px-3',
                // Only show the wrapper's visible chrome once peaks has actually
                // painted. Keeps the container in the DOM (peaks needs it for
                // init) but hides the empty strip when the wave fetch or peaks
                // init silently fails.
                showWave ? 'shadow-sm border' : 'border-0 shadow-none',
                showWave && cls.cardBg,
                showWave && cls.border
              )}
              style={{
                height: showWave ? undefined : 0,
                opacity: showWave ? 1 : 0
              }}
              aria-hidden={!showWave}
            >
              <AudioWaveform isCaptionEditor={true} session={session} />
            </div>
          )}

          {/* Split view mounts its own popover inside
              EditableSplitCaptionPanel so the FindReplaceHost runs
              under the TRANSLATION `useCaption()` scope — the pane
              being edited. The outer host here reads the source
              document, so a Replace All in split view would otherwise
              target the source (read-only) side and the user would
              see matches but no changes. */}
          {!(splitView && hasTranslations) && <CaptionFindReplacePortal />}
        </CommentFilterProvider>
      </CaptionTrainingDialogProvider>
    </CaptionEditorProvider>
  );
}

function CaptionFindReplacePortal() {
  const { searchOpen, closeSearch } = useCaptionHotkeys();
  if (!searchOpen) return null;
  return (
    <AnchoredOverlay anchorKey="search">
      <CaptionFindReplaceHost onClose={closeSearch} />
    </AnchoredOverlay>
  );
}
